import { useMemo } from "react";

import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { usePhoneNumberFormatter } from "@/ContactDirectory/Hooks/usePhoneNumberFormatter";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models//DirectoryHealthCareProvider";
import { DirectorySelectableEntity, IDirectoryFaxInfo } from "@/ContactDirectory/Models//DirectorySelectableEntity";

import commonStyles from "../FaxDirectoryForm.module.scss";
import styles from "./FaxDirectorySelectedEntityComponent.module.scss";

interface IFaxDirectorySelectedHealthcareComponentProps {
    healthcare: DirectorySelectableEntity<DirectoryHealthCareProvider>;
    setSelectedNumberId: (id: string) => void;
    selectedNumberId: string;
    close: () => void;
}

function ContactComponent(props: { fax: IDirectoryFaxInfo }): JSX.Element {
    const { fax } = props;
    const { getText } = useTranslation();
    const formatter = usePhoneNumberFormatter(fax.faxNumber.countryId);
    return (
        <div>
            <div className="ellipsis-auto">{fax.name}</div>
            <div className="ellipsis-auto">{fax.address}</div>
            <div className="ellipsis-auto">{formatter(fax.faxNumber)}</div>
            <div className="ellipsis-auto italicFont" title={fax.faxNumber.description}>{fax.faxNumber.description}</div>
            {props.fax.faxNumber.isObsoleteFaxNumberLink && (
                <div className={commonStyles.dangerColor}>
                    {getText("ContactDirectory_OutdatedFaxNumberLinkWarning")}
                </div>
            )}
        </div>
    );
}

function FaxDirectorySelectedHealthcareComponent(props: IFaxDirectorySelectedHealthcareComponentProps): JSX.Element {
    const { getText } = useTranslation();
    const faxInfoAddresses = useMemo(
        () => props.healthcare.faxInfos
            ?.sortByProperty(p => [p.address ? 0 : 1, p.address ?? ""])
            ?.distinctByProperty(p => p.faxNumber.number)
            ?.filter(p => !IsNullOrWhiteSpace(p?.faxNumber?.number) && !IsNullOrWhiteSpace(p?.faxNumber?.countryId))
            .map(fax => new InlineOption(fax.faxNumber.id, <ContactComponent fax={fax} />))
            ?? [],
        [props.healthcare.faxInfos]
    );

    const displayName = useMemo(() => props.healthcare.model.getDisplayName(), [props.healthcare.model]);

    return (
        <div>
            <div className="flex-display flex-align-items-center flex-col-gap-xs">
                <div title={displayName} className={`bold blue ${styles.displayName}`}>{displayName}</div>
                <RemoveButton onRemove={() => props.close()} className={styles.closeButton} />
            </div>
            {faxInfoAddresses?.any() && (
                <>
                    <div className="bold">{getText("FaxNumberLabel")}</div>
                    <InlineOptionsListControl
                        id="health-care-provider-fax-number-selection"
                        items={faxInfoAddresses}
                        onChange={item => props.setSelectedNumberId(item.Key)}
                        value={props.selectedNumberId}
                        style={{ marginTop: "6px", alignItems: "flex-start" }}
                        inputStyle={{ marginTop: "3px" }}
                        isMultipleLines={true}
                        labelStyle={{ width: "100%" }}
                    />
                </>
            )}
        </div>
    );
}

export { ContactComponent, FaxDirectorySelectedHealthcareComponent };
